<template>
  <admin-layout :title="$t('Sales')">
    <div class="px-4 md:px-0">
      <tec-section-title class="mb-6">
        <template #title>{{ $t('Sales') }}</template>
        <template #description>{{ $t('Please review the data in the table below') }}</template>
      </tec-section-title>

      <div class="mb-6 flex justify-between items-center">
        <search-filter v-model="form.search" class="w-full max-w-md mr-4" @reset="reset">
          <label class="block text-gray-700">{{ $t('Start Date') }}:</label>
          <text-input type="date" format="yyyy-mm-dd" v-model="form.start_date" class="mt-1 w-full" />
          <label class="mt-6 block text-gray-700">{{ $t('End Date') }}:</label>
          <text-input type="date" format="yyyy-mm-dd" v-model="form.end_date" class="mt-1 w-full" />
          <label class="mt-6 block text-gray-700">{{ $t('Status') }}:</label>
          <select-input v-model="form.status" class="mt-1 w-full">
            <option :value="null" />
            <template v-for="status in ['Pending', 'Paid', 'Overdue', 'Canceled']" :key="status">
              <option :value="status">{{ $t(status) }}</option>
            </template>
          </select-input>
          <label class="mt-6 block text-gray-700">{{ $t('Trashed') }}:</label>
          <select-input v-model="form.trashed" class="mt-1 w-full">
            <option :value="null" />
            <option value="with">{{ $t('With Trashed') }}</option>
            <option value="only">{{ $t('Only Trashed') }}</option>
          </select-input>
        </search-filter>
        <div class="flex items-center gap-2">
          <!-- <tec-button @click="exportExcel()" class="cursor-pointer">
            <span class="hidden lg:inline">{{ $t('CSV') }}</span>
          </tec-button>
          <tec-button @click="exportPDF()" class="cursor-pointer">
            <span class="hidden lg:inline">{{ $t('PDF') }}</span>
          </tec-button> -->
          <tec-dropdown align="right" width="48" v-if="$can(['create-customers', 'create-users', 'create-roles'])">
            <template #trigger>
              <button
                class="flex items-center text-sm py-1 px-2 border-2 border-transparent rounded-md focus:outline-none focus:border-gray-300 transition duration-150 ease-in-out"
              >
                <icons name="col"></icons>
              </button>
            </template>

            <template #content>
              <tec-dropdown-link as="button" v-for="(col, ci) in Object.keys(columns)" :key="ci" @click="toggleCol(col)">
                <span class="flex items-center gap-2">
                  <icons name="tick" class="w-3 h-3" v-if="columns[col]"></icons>
                  <icons name="cross" class="w-3 h-3" v-else></icons>
                  <span class="capitalize">{{ $t(col.replace('_', ' ')) }}</span>
                </span>
              </tec-dropdown-link>
            </template>
          </tec-dropdown>
          <tec-dropdown align="right" width="48" v-if="$can(['create-customers', 'create-users', 'create-roles'])">
            <template #trigger>
              <button
                class="flex items-center text-sm py-1 px-2 border-2 border-transparent rounded-md focus:outline-none focus:border-gray-300 transition duration-150 ease-in-out"
              >
                <icons name="save" class="-mt-1 mr-1"></icons>
                Export
              </button>
            </template>

            <template #content>
              <tec-dropdown-link as="button" @click="exportCSV()">
                {{ $t('Export as CSV') }}
              </tec-dropdown-link>
              <tec-dropdown-link as="button" @click="exportPDF()">
                {{ $t('Export as PDF') }}
              </tec-dropdown-link>
            </template>
          </tec-dropdown>
          <tec-button :href="route('sales.create')">
            <span>
              <icons name="plus" class="w-5 h-5 lg:mr-2" />
            </span>
            <span class="hidden lg:inline">{{ $t('create_x', { x: $t('Sale') }) }}</span>
          </tec-button>
        </div>
      </div>
      <div class="bg-white rounded-md shadow overflow-x-scroll h-full">
        <table class="w-full whitespace-nowrap" id="salesTable">
          <tr class="text-left font-bold">
            <th v-if="columns.number" class="px-6 pt-6 pb-4">{{ $t('Number') }}</th>
            <th v-if="columns.locator" class="px-6 pt-6 pb-4">{{ $t('Locator') }}</th>
            <th v-if="columns.date" class="px-6 pt-6 pb-4">{{ $t('Date') }}</th>
            <th v-if="columns.customer" class="px-6 pt-6 pb-4">{{ $t('Customer') }}</th>
            <th v-if="columns.agent" class="px-6 pt-6 pb-4">{{ $t('Agent') }}</th>
            <th v-if="columns.total" class="px-6 pt-6 pb-4">{{ $t('Total') }}</th>
            <th v-if="columns.tax" class="px-6 pt-6 pb-4">{{ $t('Tax') }}</th>
            <th v-if="columns.commission" class="px-6 pt-6 pb-4">{{ $t('Commission') }}</th>
            <th v-if="columns.agent_commission" class="px-6 pt-6 pb-4">{{ $t('Agent Commission') }}</th>
            <th v-if="columns.status" class="px-6 pt-6 pb-4">{{ $t('Status') }}</th>
            <th v-if="columns.created_at" class="px-6 pt-6 pb-4">{{ $t('Created at') }}</th>
            <th v-if="columns.user" class="px-6 pt-6 pb-4">{{ $t('Created by') }}</th>
            <th class="px-6 pt-6 pb-4 w-8"></th>
          </tr>
          <tr
            :key="sale.id"
            v-for="(sale, si) in sales.data"
            class="hover:bg-gray-100 focus-within:bg-gray-100 even:bg-white odd:bg-gray-50"
            :class="{ 'cursor-pointer': $can('update-sales') }"
          >
            <td @click="goto(sale.id)" v-if="columns.number" class="border-t">
              <div class="px-6 py-4 flex items-center">
                <span class="font-bold">{{ sale.number }}</span>
                <icons v-if="sale.deleted_at" name="trash" class="flex-shrink-0 w-4 h-4 fill-red-400 ml-2" />
              </div>
            </td>
            <td @click="goto(sale.id)" v-if="columns.locator" class="border-t">
              <div class="px-6 py-4">{{ sale.locator_number }}</div>
            </td>
            <td @click="goto(sale.id)" v-if="columns.date" class="border-t">
              <div class="px-6 py-4">{{ $date(sale.date) }}</div>
            </td>
            <td @click="goto(sale.id)" v-if="columns.customer" class="border-t">
              <div class="px-6 py-4">{{ sale?.customer?.name }}</div>
            </td>
            <td @click="goto(sale.id)" v-if="columns.agent" class="border-t">
              <div class="px-6 py-4">{{ sale?.agent?.name }}</div>
            </td>
            <td @click="goto(sale.id)" v-if="columns.total" class="border-t">
              <div class="px-6 py-4 text-right">
                <span class="font-extrabold">{{ $number(sale.grand_total) }}</span>
              </div>
            </td>
            <td @click="goto(sale.id)" v-if="columns.tax" class="border-t">
              <div class="px-6 py-4 text-right">
                <span class="font-extrabold">{{ $number(sale.total_tax) }}</span>
              </div>
            </td>
            <td @click="goto(sale.id)" v-if="columns.commission" class="border-t">
              <div class="px-6 py-4 text-right">
                <span class="font-extrabold">{{ $number(sale.total_commission) }}</span>
              </div>
            </td>
            <td @click="goto(sale.id)" v-if="columns.agent_commission" class="border-t">
              <div class="px-6 py-4 text-right">
                <span class="font-extrabold">{{ $number(sale.total_agent_commission) }}</span>
              </div>
            </td>
            <td @click="goto(sale.id)" v-if="columns.status" class="border-t">
              <div class="px-6 py-4">
                <Status :status="sale.status" />
              </div>
            </td>
            <td @click="goto(sale.id)" v-if="columns.created_at" class="border-t">
              <div class="px-6 py-4">{{ $datetime(sale.created_at) }}</div>
            </td>
            <td @click="goto(sale.id)" v-if="columns.user" class="border-t">
              <div class="px-6 py-4">{{ sale?.user?.name }}</div>
            </td>
            <td class="border-t w-8">
              <div class="px-6 py-4">
                <tec-dropdown align="bottom-right" width="40" v-if="$can(['create-customers', 'create-users', 'create-roles'])">
                  <template #trigger>
                    <button
                      class="flex items-center text-sm py-1 px-2 border-2 border-transparent rounded-md focus:outline-none focus:border-gray-300 transition duration-150 ease-in-out"
                    >
                      <icons name="vdots"></icons>
                    </button>
                  </template>

                  <template #content>
                    <div class="relative z-20">
                      <div
                        class="absolute right-0 bg-white w-48 rounded-md border -my-1"
                        :class="si + 2 < sales.data.length ? ' -translate-y-1/2' : ' top-2 -translate-y-full '"
                      >
                        <tec-dropdown-link :href="route('sales.show', { sale: sale.id })">
                          <span class="flex items-center gap-2">
                            <icons name="doc"></icons>
                            {{ $t('View Sale') }}
                          </span>
                        </tec-dropdown-link>
                        <!-- <tec-dropdown-link as="button" @click="goto(sale.id)">
                        {{ $t('View Sale Modal') }}
                      </tec-dropdown-link> -->
                        <tec-dropdown-link :href="route('notifications.sale', sale.id)">
                          <span class="flex items-center gap-2">
                            <icons name="email"></icons>
                            {{ $t('Email Sale') }}
                          </span>
                        </tec-dropdown-link>
                        <tec-dropdown-link as="button" @click="download(sale.id)">
                          <span class="flex items-center gap-2">
                            <icons name="save"></icons>
                            {{ $t('Download Sale') }}
                          </span>
                        </tec-dropdown-link>
                        <tec-dropdown-link :href="route('sales.edit', { sale: sale.id })">
                          <span class="flex items-center gap-2 text-yellow-600">
                            <icons name="edit"></icons>
                            {{ $t('Edit Sale') }}
                          </span>
                        </tec-dropdown-link>
                        <tec-dropdown-link :href="route('sales.destroy', sale.id)">
                          <span class="flex items-center gap-2 text-red-600">
                            <icons name="trash"></icons>
                            {{ $t('Delete Sale') }}
                          </span>
                        </tec-dropdown-link>
                      </div>
                    </div>
                  </template>
                </tec-dropdown>
                <!-- <icons name="vdots" /> -->
              </div>
            </td>
            <!-- <td class="border-t">
              <div class="px-6 py-4">
                <div class="flex items-center">
                  <span class="text-gray-500 mr-1">{{ $t('Number') }}:</span>
                  <span class="font-extrabold">{{ sale.id }}</span>
                  <icons v-if="sale.deleted_at" name="trash" class="flex-shrink-0 w-4 h-4 fill-red-400 ml-2" />
                </div>
                <div class="flex items-center">
                  <span class="text-gray-500 mr-1">{{ $t('Locator') }}:</span>
                  {{ sale.locator_number }}
                </div>
                <div class="flex items-center">
                  <span class="text-gray-500 mr-1">{{ $t('Date') }}:</span>
                  {{ $date(sale.date) }}
                </div>
              </div>
            </td>
            <td class="border-t">
              <div class="px-6 py-4">
                <div class="flex items-center">
                  <span class="text-gray-500 mr-1">{{ $t('Total Tax') }}:</span>
                  {{ $number(sale.total_tax) }}
                </div>
                <div class="flex items-center">
                  <span class="text-gray-500 mr-1">{{ $t('Grand Total') }}:</span>
                  <span class="font-extrabold">{{ $number(sale.grand_total) }}</span>
                </div>
                <div class="flex items-center">
                  <span class="text-gray-500 mr-1">{{ $t('Status') }}:</span>
                  <span class="font-extrabold">{{ $t(sale.status) }}</span>
                </div>
              </div>
            </td>
            <td class="border-t">
              <div class="px-6 py-4">
                <div class="flex items-center">
                  <span class="text-gray-500 mr-1">{{ $t('Commission') }}:</span>
                  {{ $number(sale.total_commission) }}
                </div>
                <div class="flex items-center">
                  <span class="text-gray-500 mr-1">{{ $t('Agent Commission') }}:</span>
                  {{ $number(sale.total_agent_commission) }}
                </div>
              </div>
            </td>
            <td class="border-t">
              <div class="px-6 py-4">
                <div class="flex items-center">
                  <span class="text-gray-500 mr-1">{{ $t('Customer') }}:</span>
                  {{ sale?.customer?.name }}
                </div>
                <div class="flex items-center">
                  <span class="text-gray-500 mr-1">{{ $t('User') }}:</span>
                  {{ sale?.user?.name }}
                </div>
              </div>
            </td>
            <td class="border-t w-px">
              <div v-if="$can('update-sales')" class="px-4 flex items-center">
                <icons name="chevron-down" class="transform -rotate-90 block w-4 h-4" />
              </div>
            </td> -->
          </tr>
          <tr v-if="sales.length === 0">
            <td class="border-t px-6 py-4" colspan="4">{{ $t('There is no data to display.') }}</td>
          </tr>
        </table>
      </div>
      <pagination class="mt-6" :meta="sales.meta" :links="sales.links" />
    </div>

    <modal :show="details" max-width="4xl" :closeable="true" @close="hideDetails">
      <div class="px-6 py-4 print:px-0">
        <div v-if="details && sale" class="flex items-center justify-between print:hidden">
          <div class="text-lg w-full flex items-center justify-between">
            <div>
              {{ $t('Sale Details') }} <span class="hidden sm:inline">(Id: {{ sale.id }})</span>
            </div>
            <div class="flex">
              <button
                @click="print()"
                class="flex items-center justify-center mr-2 h-8 w-8 rounded-full text-gray-600 hover:text-gray-800 hover:bg-gray-300 focus:outline-none"
              >
                <icons name="printer" class="h-5 w-5" />
              </button>
              <Link
                v-if="$can('read-sales')"
                :href="route('notifications.sale', sale.id)"
                class="flex items-center justify-center mr-2 h-8 w-8 rounded-full text-gray-600 hover:text-gray-800 hover:bg-gray-300 focus:outline-none"
              >
                <icons name="email" class="h-5 w-5" />
              </Link>
              <Link
                v-if="$can('update-sales')"
                :href="route('sales.edit', sale.id)"
                class="flex items-center justify-center mr-2 h-8 w-8 rounded-full text-gray-600 hover:text-gray-800 hover:bg-gray-300 focus:outline-none"
              >
                <icons name="edit" class="h-5 w-5" />
              </Link>
            </div>
          </div>
          <div class="-mr-2 flex items-center">
            <!-- <button
              @click="print()"
              class="flex items-center justify-center mr-2 h-8 w-8 rounded-full text-gray-600 hover:text-gray-800 hover:bg-gray-300 focus:outline-none"
            >
              <icons name="printer" class="h-5 w-5" />
            </button>
            <inertia-link
              v-if="$can('update-sales')"
              :href="route('sales.edit', sale.id)"
              class="flex items-center justify-center mr-2 h-8 w-8 rounded-full text-gray-600 hover:text-gray-800 hover:bg-gray-300 focus:outline-none"
            >
              <icons name="edit" class="h-5 w-5" />
            </inertia-link> -->
            <button
              @click="hideDetails()"
              class="flex items-center justify-center h-8 w-8 rounded-full text-gray-600 hover:text-gray-800 hover:bg-gray-300 focus:outline-none"
            >
              <icons name="cross" class="h-5 w-5" />
            </button>
          </div>
        </div>

        <div class="mt-4 print-mt-0">
          <sale-details v-if="sale" :sale="sale" />
        </div>
      </div>
    </modal>
    <iframe id="txtArea1" style="display: none"></iframe>
  </admin-layout>
</template>

<script>
import 'jspdf-autotable';
import { jsPDF } from 'jspdf';
import Status from './Status.vue';
import pickBy from 'lodash/pickBy';
import throttle from 'lodash/throttle';
import TecButton from '@/Shared/Button';
import SaleDetails from './Details.vue';
import mapValues from 'lodash/mapValues';
import Modal from '@/Jetstream/Modal.vue';
import TextInput from '@/Shared/TextInput';
import Pagination from '@/Shared/Pagination';
import TecDropdown from '@/Jetstream/Dropdown';
import SelectInput from '@/Shared/SelectInput';
import AdminLayout from '@/Layouts/AdminLayout';
import SearchFilter from '@/Shared/SearchFilter';
import { Head, Link } from '@inertiajs/inertia-vue3';
import TecDropdownLink from '@/Jetstream/DropdownLink';
import TecSectionTitle from '@/Jetstream/SectionTitle';

export default {
  components: {
    Link,
    Head,
    Modal,
    Status,
    TecButton,
    TextInput,
    Pagination,
    SaleDetails,
    AdminLayout,
    SelectInput,
    TecDropdown,
    SearchFilter,
    TecDropdownLink,
    TecSectionTitle,
  },

  props: {
    filters: Object,
    sales: Object,
  },

  data() {
    return {
      columns: {
        number: true,
        locator: true,
        date: true,
        customer: true,
        agent: false,
        total: true,
        tax: true,
        commission: true,
        agent_commission: true,
        status: true,
        created_at: false,
        user: false,
      },
      sale: null,
      details: false,
      form: {
        role: this.filters.role,
        search: this.filters.search,
        trashed: this.filters.trashed,
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
      },
    };
  },

  watch: {
    form: {
      handler: throttle(function () {
        let query = pickBy(this.form);
        this.$inertia.get(this.route('sales', Object.keys(query).length ? query : { remember: 'forget' }), {}, { preserveState: true });
      }, 500),
      deep: true,
    },
  },

  methods: {
    // goto(link) {
    //   if (this.$can('update-sales')) {
    //     this.$inertia.visit(link);
    //   }
    // },
    reset() {
      this.form = mapValues(this.form, () => null);
    },
    goto(id) {
      if (this.sale && this.sale.id == id) {
        this.details = true;
      } else {
        this.loading = true;
        axios.get(route('sales.show', id) + '?json=yes').then(res => {
          this.sale = res.data;
          this.details = true;
          this.loading = false;
        });
      }
      // if (this.$can('read-checkins')) {
      //   this.$inertia.visit(route('checkins.show', id));
      // }
    },
    showDetails() {
      this.details = false;
    },
    hideDetails() {
      this.details = false;
    },
    print() {
      window.print();
    },
    toggleCol(v) {
      this.columns[v] = !this.columns[v];
    },
    download(id) {
      window.location.href = this.route('sales.download', { sale: id });
    },
    exportPDF() {
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.text('Sales', 15, 15);
      //   doc.autoTable({ html: '#salesTable', theme: 'grid', margin: { top: 20 } });
      let sales = this.sales.data.map(s => {
        return {
          number: s.number,
          locator_number: s.locator_number,
          date: this.$date(s.date),
          customer: s.customer.name,
          grand_total: this.$number(s.grand_total),
          total_tax: this.$number(s.total_tax),
          total_commission: this.$number(s.total_commission),
          total_agent_commission: this.$number(s.total_agent_commission),
          status: s.status,
        };
      });
      //   console.log(sales);
      doc.autoTable({
        theme: 'grid',
        margin: { top: 20 },
        columnStyles: { 4: { halign: 'right', fontStyle: 'bold' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' } },
        head: [['Number', 'Locator', 'Date', 'Customer', 'Total', 'Tax', 'Commission', 'Agent Commission', 'Status']],
        body: sales.map(s => [
          s.number,
          s.locator_number,
          s.date,
          s.customer,
          s.grand_total,
          s.total_tax,
          s.total_commission,
          s.total_agent_commission,
          s.status,
        ]),
        // columns: [
        //   { header: 'Number', dataKey: 'number' },
        //   { header: 'Locator', dataKey: 'locator_number' },
        //   { header: 'Date', dataKey: 'date' },
        //   { header: 'Customer', dataKey: 'customer' },
        //   { header: 'Total', dataKey: 'grand_total' },
        //   { header: 'Tax', dataKey: 'total_tax' },
        //   { header: 'Commission', dataKey: 'total_commission' },
        //   { header: 'Agent Commission', dataKey: 'total_agent_commission' },
        //   { header: 'status', dataKey: 'status' },
        // ],
      });

      doc.save('salesTable_' + new Date().toLocaleDateString() + '.pdf');
    },
    exportCSV() {
      // Select rows from table_id
      let table_id = 'salesTable';
      let separator = ',';
      var rows = document.querySelectorAll('table#' + table_id + ' tr');
      // Construct csv
      var csv = [];
      for (var i = 0; i < rows.length; i++) {
        var row = [],
          cols = rows[i].querySelectorAll('td, th');
        for (var j = 0; j < cols.length; j++) {
          // Clean innertext to remove multiple spaces and jumpline (break csv)
          var data = cols[j].innerText.replace(/(\r\n|\n|\r)/gm, '').replace(/(\s\s)/gm, ' ');
          // Escape double-quote with double-double-quote (see https://stackoverflow.com/questions/17808511/properly-escape-a-double-quote-in-csv)
          data = data.replace(/"/g, '""');
          // Push escaped string
          row.push('"' + data + '"');
        }
        csv.push(row.join(separator));
      }
      var csv_string = csv.join('\n');
      // Download it
      var filename = table_id + '_' + new Date().toLocaleDateString() + '.csv';
      var link = document.createElement('a');
      link.style.display = 'none';
      link.setAttribute('target', '_blank');
      link.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv_string));
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      //   let tab_text = "<table border='2px'><tr bgcolor='#87AFC6'>";
      //   let textRange;
      //   let j = 0;
      //   let tab = document.getElementById('salesTable'); // id of table

      //   for (j = 0; j < tab.rows.length; j++) {
      //     tab_text = tab_text + tab.rows[j].innerHTML + '</tr>';
      //     //tab_text=tab_text+"</tr>";
      //   }

      //   tab_text = tab_text + '</table>';
      //   tab_text = tab_text.replace(/<A[^>]*>|<\/A>/g, ''); //remove if u want links in your table
      //   tab_text = tab_text.replace(/<img[^>]*>/gi, ''); // remove if u want images in your table
      //   tab_text = tab_text.replace(/<input[^>]*>|<\/input>/gi, ''); // reomves input params

      //   let sa;
      //   let ua = window.navigator.userAgent;
      //   let msie = ua.indexOf('MSIE ');

      //   if (msie > 0 || !!navigator.userAgent.match(/Trident.*rv\:11\./)) {
      //     // If Internet Explorer
      //     txtArea1.document.open('txt/html', 'replace');
      //     txtArea1.document.write(tab_text);
      //     txtArea1.document.close();
      //     txtArea1.focus();
      //     sa = txtArea1.document.execCommand('SaveAs', true, 'Say Thanks to Sumit.xls');
      //   } //other browser not tested on IE 11
      //   else sa = window.open('data:application/vnd.ms-excel,' + encodeURIComponent(tab_text));

      //   return sa;
    },
  },
};
</script>
